import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Briefcase, ArrowRight, CheckCircle, TrendingUp,
  Globe, Shield, DollarSign, Users,
  BarChart3, RefreshCw,
  Target, ArrowRightLeft, ClipboardCheck, Droplets, Zap
} from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';
import PillarStrip from '../common/PillarStrip';
import { PILLARS } from '../../config/pillars';
import useMetaTags from '../../hooks/useMetaTags';


// Unified header styling
const HEADER_BG = 'bg-[#4D4D4D]';
const HEADER_HEIGHT = 'h-24';

// Product Card Component with architectural styling
const ProductCard = ({ product }) => (
  <div className="bg-white rounded-none border border-zinc-300 overflow-hidden hover:border-[#4D4D4D] transition-all duration-300 group flex flex-col h-full">
    <div className={`${HEADER_BG} px-6 ${HEADER_HEIGHT} flex items-center justify-between border-b border-[#4D4D4D]`}>
      <div>
        <h3 className="font-sans text-xl font-bold text-white">{product.title}</h3>
        <p className="text-zinc-300 text-xs font-mono mt-1">{product.subtitle}</p>
      </div>
      {product.apr && (
        <div className="text-right">
          <p className="text-2xl font-bold text-white font-mono">{product.apr}</p>
          <p className="text-zinc-300 text-[10px] font-mono uppercase tracking-wider">APR (USD)</p>
        </div>
      )}
    </div>
    <div className="p-6 flex flex-col flex-grow">
      <p className="text-xs font-sans text-zinc-600 mb-6 leading-relaxed">{product.description}</p>

      {product.specs && (
        <div className="space-y-3 mb-6">
          {product.specs.map((spec, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <div className="w-7 h-7 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center flex-shrink-0">
                <spec.icon className="w-3.5 h-3.5 text-black" />
              </div>
              <span className="text-xs font-sans text-zinc-700">{spec.text}</span>
            </div>
          ))}
        </div>
      )}

      {product.features && (
        <div className="space-y-2 mb-6">
          {product.features.map((feature, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <CheckCircle className="w-3.5 h-3.5 text-black flex-shrink-0 mt-0.5" />
              <span className="text-xs font-sans text-zinc-600">{feature}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex-grow"></div>

      {product.bestFor && (
        <div className="bg-zinc-50 border border-zinc-200 p-3 mb-6 rounded-none">
          <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400 mb-1">Best For</p>
          <p className="text-xs font-medium text-black font-sans">{product.bestFor}</p>
        </div>
      )}

      <Link
        to={product.ctaLink || '/register'}
        className="flex items-center justify-center gap-2 w-full py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all"
      >
        {product.ctaText || 'Get Started'} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  </div>
);

// Bank Deal Flow Card
const BankDealCard = ({ deal }) => (
  <div className="bg-white rounded-none border border-zinc-300 overflow-hidden hover:border-[#4D4D4D] transition-all duration-300 flex flex-col h-full">
    <div className={`${HEADER_BG} px-6 ${HEADER_HEIGHT} flex items-center border-b border-[#4D4D4D]`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 border border-zinc-400 bg-white/10 rounded-none flex items-center justify-center">
          <deal.icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-sans text-xl font-bold text-white">{deal.title}</h3>
          <p className="text-zinc-300 text-xs font-mono">{deal.subtitle}</p>
        </div>
      </div>
    </div>
    <div className="p-6 flex flex-col flex-grow">
      <p className="text-xs font-sans text-zinc-600 mb-6 leading-relaxed">{deal.description}</p>
      <div className="space-y-3 flex-grow">
        {deal.benefits.map((benefit, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <CheckCircle className="w-4 h-4 text-black flex-shrink-0 mt-0.5" />
            <span className="text-xs font-sans text-zinc-700">{benefit}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Section Header Component
const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className={`${HEADER_BG} px-6 ${HEADER_HEIGHT} flex items-center border-b border-[#4D4D4D]`}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 border border-zinc-400 bg-white/10 rounded-none flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h3 className="font-sans text-xl font-bold text-white">{title}</h3>
        <p className="text-zinc-300 text-xs font-mono">{subtitle}</p>
      </div>
    </div>
  </div>
);

const ProductsPage = () => {
  useMetaTags({
    title: 'ForwardsFlow | Platform',
    description: 'Seven capabilities. One cashflow stack. Yield discovery, exchange rates, compliance fulfilment, hedging devices, liquidity preservation, transaction execution and portfolio management.',
    canonical: 'https://www.forwardsflow.com/products',
  });

  const investorProducts = [
    {
      title: 'Frontier Economy T-Bill Platform',
      subtitle: 'Frontier economy treasury bills with automated rollover',
      description: 'Invest in frontier economy treasury bills with automated 3-month rollover, exchange rate forwards protection on divestment, and pooled forwards for better pricing. Direct depository settlement with a portfolio dashboard and auction calendar.',
      specs: [
        { icon: RefreshCw, text: '91-day T-bills with automated 3-month rollover' },
        { icon: Shield, text: 'Exchange rate forwards protection on divestment' },
        { icon: TrendingUp, text: 'Pooled forwards for better pricing' },
      ],
      features: [
        'Direct depository settlement',
        'Portfolio dashboard with auction calendar',
        'Automated rollover at each 91-day maturity',
        'Pooled foreign exchange forwards for competitive divestment pricing',
      ],
      bestFor: 'International investors',
      ctaLink: '/register/investor',
      ctaText: 'Open Account',
    },
  ];

  const bankDeals = [
    {
      title: 'T-Bill Distribution Platform',
      subtitle: 'End-to-end T-bill distribution infrastructure',
      icon: Building2,
      description: 'Enable international clients to invest in frontier economy treasury bills through your bank with pre-KYC bulk account opening, digital depository account setup, and integrated foreign exchange and forwards quote provision.',
      benefits: [
        'Pre-KYC bulk account opening for international clients',
        'Digital depository account setup in the loop',
        'Foreign exchange and forwards quote provision',
        'NOSTRO settlement for foreign exchange transactions',
        'Dealer workstation for foreign exchange/forwards pricing',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Home</Link>
              <Link to="/products" className="font-mono text-xs uppercase tracking-[0.15em] text-black font-bold border-b-2 border-black pb-1">Platform</Link>
              <Link to="/register" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Open Account</Link>
              <Link to="/demo" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Book a Demo</Link>
              <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Pricing</Link>
              <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>
              <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#4D4D4D] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 block mb-2">Platform capabilities</span>
          <h1 className="font-sans text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">Seven capabilities. One cashflow stack.</h1>
          <p className="font-sans text-base text-zinc-300 max-w-2xl mx-auto">
            Yield discovery, exchange rates, compliance fulfilment, hedging devices, liquidity preservation, transaction execution and portfolio management — one platform for cross-border cashflows across 6 markets.
          </p>
        </div>
      </section>

      {/* Pillar Strip Navigation */}
      <section className="sticky top-20 z-40 bg-white border-b border-zinc-200 py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <PillarStrip variant="compact" />
        </div>
      </section>

      {/* Pillar Sections */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 space-y-20">
        {PILLARS.map((pillar) => {
          const PILLAR_ICON_MAP = { Target, ArrowRightLeft, ClipboardCheck, Shield, Droplets, Zap, Briefcase };
          const Icon = PILLAR_ICON_MAP[pillar.icon];
          return (
            <section key={pillar.key} id={pillar.key} className="scroll-mt-40">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 border border-zinc-300 flex items-center justify-center flex-shrink-0 bg-zinc-50">
                  {Icon && <Icon className="w-5 h-5 text-black" />}
                </div>
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 block mb-1">{String(pillar.n).padStart(2, '0')}</span>
                  <h2 className="font-sans text-2xl font-bold text-black">{pillar.label}</h2>
                  <p className="text-sm text-zinc-600 mt-1 font-sans max-w-2xl">{pillar.blurb}</p>
                </div>
              </div>
              <div className="border border-zinc-200 p-6 bg-zinc-50">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 block mb-3">Investor side</span>
                    <p className="text-xs text-zinc-700 leading-relaxed font-sans">{pillar.blurb}</p>
                  </div>
                  {pillar.bankRoles.length > 0 && (
                    <div>
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 block mb-3">Bank side</span>
                      <p className="text-xs text-zinc-700 leading-relaxed font-sans">
                        Served by: {pillar.bankRoles.map(r => r.replace('bank_', '').replace(/_/g, ' ')).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })}

        {/* Existing product cards for context */}
        <section className="border-t border-zinc-200 pt-16">
          <div className="text-center mb-12">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Investment products</span>
            <h2 className="font-sans text-2xl font-bold text-black mb-2">For Investors</h2>
          </div>
          <div className="max-w-xl mx-auto">
            {investorProducts.map((product, idx) => (
              <ProductCard key={idx} product={product} />
            ))}
          </div>
        </section>

        <section className="border-t border-zinc-200 pt-16">
          <div className="text-center mb-12">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Bank solutions</span>
            <h2 className="font-sans text-2xl font-bold text-black mb-2">For Banks</h2>
          </div>
          <div className="max-w-xl mx-auto">
            {bankDeals.map((deal, idx) => (
              <BankDealCard key={idx} deal={deal} />
            ))}
          </div>

          {/* Bank CTA */}
          <div className="mt-16 bg-[#4D4D4D] text-white border border-[#4D4D4D] p-8 md:p-12 rounded-none">
            <div className="max-w-2xl mx-auto text-center">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 block mb-2">Bank Partner Onboarding</span>
              <h3 className="font-sans text-2xl font-bold text-white mb-4">Distribute T-bills to your international clients</h3>
              <p className="text-sm font-sans text-zinc-200 mb-6 leading-relaxed">
                Partner with Forwardsflow to offer frontier economy treasury bill investments to your international clients. Our platform handles pre-KYC bulk account opening, digital depository account setup, foreign exchange quote provision, and NOSTRO settlement.
              </p>
              <Link to="/register/bank" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-mono text-xs uppercase tracking-[0.15em] font-medium border border-white hover:bg-zinc-100 transition-all rounded-none">
                Register Your Bank <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;
